import IndigoApi from 'communication/IndigoApi';
import { WidgetType } from 'widgets/WidgetType';
import { ClickstreamUsageByTime } from 'clickstream/ClickstreamUsageByTime';
import { ApiRequestOptions } from 'communication/ApiCallInfo';
import RequestProgressService from 'requestProgress/RequestProgressService';
import { MemoryMonitoringService } from 'performance/MemoryMonitoringService';

interface ClickstreamServiceOptions {
    indigoApi: IndigoApi;
    requestProgressService: RequestProgressService;
    memoryMonitoring: MemoryMonitoringService;
}

export interface ClickstreamEvent<TEvent> {
    timestamp: number;
    type: ClickstreamEventType;
    event: TEvent;
}

export enum ClickstreamEventType {
    USAGE_BY_TIME = 'UsageByTime',
    ERROR = 'Error',
    FILLS_GRAPH_CONFIG_CHANGED = 'FillsGraphConfigChange',
    MEASURED_MEMORY = 'MeasuredMemory',
}

export default class ClickstreamService {
    private indigoApi: IndigoApi;
    private requestProgressService: RequestProgressService;
    private usageByTime: ClickstreamUsageByTime;
    private memoryMonitoring: MemoryMonitoringService;

    public constructor(options: ClickstreamServiceOptions) {
        this.indigoApi = options.indigoApi;
        this.requestProgressService = options.requestProgressService;
        this.usageByTime = new ClickstreamUsageByTime(this);
        this.memoryMonitoring = options.memoryMonitoring;

        setInterval(this.recordMemory.bind(this), 300000);
    }

    public postEvent<T>(type: ClickstreamEventType, payload: T) {
        const clickstreamEvent: ClickstreamEvent<T> = {
            timestamp: Date.now(),
            type,
            event: payload,
        };
        const apiCall = (options: ApiRequestOptions) =>
            this.indigoApi.postClickstreamEvent(clickstreamEvent, options);

        this.requestProgressService
            .invokeAndMonitorRequest('postClickstreamEvent', apiCall)
            .catch(() => {
                //We can tolerate clickstream failures
            });
    }

    public recordWidgetUsage(widgetType: WidgetType) {
        this.usageByTime.recordWidgetUsage(widgetType);
    }

    public recordWidgetFeatureUsage(widgetType: WidgetType, feature: string) {
        this.usageByTime.recordWidgetFeatureUsage(widgetType, feature);
    }

    private recordMemory() {
        const latestResult = this.memoryMonitoring.results[0];
        if (latestResult) {
            this.postEvent(ClickstreamEventType.MEASURED_MEMORY, {
                bytes: latestResult.result.bytes,
            });
        }
    }

    public flushWidgetUsage() {
        this.usageByTime.flush();
    }

    public recordWidgetUsages(widgetTypes: WidgetType[]) {
        this.usageByTime.recordWidgetUsages(widgetTypes);
    }
}
